import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import axios from "axios";
import { X, Save, Trash, Settings2 } from "lucide-react";
import { toast } from "sonner";

export default function DriverConfigQuickAddModal({
  open,
  onOpenChange,
  baseurl,
  token,
  setIsUnauthorised,
  adminId,
  drivers,            // driver list for dropdown
  onSuccess,          // refresh config table callback
}) {
  const [selectedDriverCode, setSelectedDriverCode] = useState("");
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingConfig, setExistingConfig] = useState(null);

  const [form, setForm] = useState({
    cashLimit: "",
    dailyTaskLimit: "",
    concurrentOrder: "",
    maxTeamLimit: "",
  });

  const selectedDriver = useMemo(() => {
    return (drivers || []).find((d) => d.driverId === selectedDriverCode) || null;
  }, [drivers, selectedDriverCode]);

  const resetAll = () => {
    setSelectedDriverCode("");
    setExistingConfig(null);
    setForm({
      cashLimit: "",
      dailyTaskLimit: "",
      concurrentOrder: "",
      maxTeamLimit: "",
    });
  };

  const fetchExisting = async (driverCode) => {
    if (!driverCode) return;
    setFetching(true);
    try {
      const payload = { adminId, driverCode };
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/getAllDriverConfigDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        const row = (res.data.data || []).find((x) => x.driver_code === driverCode) || null;
        setExistingConfig(row);

        if (row) {
          setForm({
            cashLimit: row.cash_limit?.toString?.() ?? "",
            dailyTaskLimit: row.daily_task_limit?.toString?.() ?? "",
            concurrentOrder: row.concurrent_order?.toString?.() ?? "",
            maxTeamLimit: row.max_team_limit?.toString?.() ?? "",
          });
        } else {
          setForm({
            cashLimit: "",
            dailyTaskLimit: "",
            concurrentOrder: "",
            maxTeamLimit: "",
          });
        }
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised?.(true);
      } else {
        toast.error(res.data.responseMessage || "Failed to fetch existing config");
      }
    } catch (e) {
      toast.error("Error fetching existing config");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (!open) resetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onDriverChange = (val) => {
    setSelectedDriverCode(val);
    setExistingConfig(null);
    setForm({
      cashLimit: "",
      dailyTaskLimit: "",
      concurrentOrder: "",
      maxTeamLimit: "",
    });

    if (val) fetchExisting(val);
  };

  const validate = () => {
    if (!selectedDriverCode) return "Please select a driver";
    const numericKeys = ["cashLimit", "dailyTaskLimit", "concurrentOrder", "maxTeamLimit"];
    for (const k of numericKeys) {
      const v = (form[k] ?? "").toString().trim();
      if (!v) return `${k} is required`;
      if (!/^\d+(\.\d+)?$/.test(v)) return `${k} must be numeric`;
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) return toast.error(err);

    setSaving(true);
    try {
      const payload = {
        adminId,
        driverCode: selectedDriverCode,
        cashLimit: String(form.cashLimit),
        dailyTaskLimit: String(form.dailyTaskLimit),
        concurrentOrder: String(form.concurrentOrder),
        maxTeamLimit: String(form.maxTeamLimit),
      };

      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/insertDriverConfigDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage || "Saved");
        onSuccess?.();
        onOpenChange(false);
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised?.(true);
      } else {
        toast.error(res.data.responseMessage || "Failed to save");
      }
    } catch (e) {
      toast.error("Error saving config");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingConfig?.pk_config_id) return toast.error("No existing config to delete");

    const ok = window.confirm(`Delete configuration for ${selectedDriverCode}?`);
    if (!ok) return;

    setSaving(true);
    try {
      const payload = { adminId, configId: String(existingConfig.pk_config_id) };
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/deleteDriverConfigDetails`,
        payload,
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success(res.data.responseMessage || "Deleted");
        onSuccess?.();
        onOpenChange(false);
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised?.(true);
      } else {
        toast.error(res.data.responseMessage || "Failed to delete");
      }
    } catch (e) {
      toast.error("Error deleting config");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-xl bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-orange-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center text-orange-700">
                <Settings2 size={18} />
              </div>
              <div>
                <Dialog.Title className="text-base font-semibold text-gray-900">
                  Add / Update Driver Configuration
                </Dialog.Title>
                <p className="text-xs text-gray-600">
                  Select driver → auto-prefill existing config
                </p>
              </div>
            </div>

            <Dialog.Close asChild>
              <button className="p-2 rounded-lg hover:bg-gray-100" aria-label="Close">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <div className="px-5 py-4 space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">
                Select Driver
              </label>
              <select
                className="w-full border rounded px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                value={selectedDriverCode}
                onChange={(e) => onDriverChange(e.target.value)}
              >
                <option value="">-- Select --</option>
                {(drivers || []).map((d) => (
                  <option key={d.driverId} value={d.driverId}>
                    {d.driverId} • {d.fullName || d.username || "-"} • {d.teamName || "-"}
                  </option>
                ))}
              </select>

              {selectedDriver && (
                <div className="mt-2 text-xs text-gray-600">
                  <b>{selectedDriver.fullName || selectedDriver.username}</b> •{" "}
                  {selectedDriver.phone || "-"} • {selectedDriver.teamName || "-"}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Cash Limit" value={form.cashLimit} onChange={(v) => setForm((p) => ({ ...p, cashLimit: v }))} />
              <Field label="Daily Task Limit" value={form.dailyTaskLimit} onChange={(v) => setForm((p) => ({ ...p, dailyTaskLimit: v }))} />
              <Field label="Concurrent Order" value={form.concurrentOrder} onChange={(v) => setForm((p) => ({ ...p, concurrentOrder: v }))} />
              <Field label="Max Team Limit" value={form.maxTeamLimit} onChange={(v) => setForm((p) => ({ ...p, maxTeamLimit: v }))} />
            </div>

            <div className="rounded-lg border bg-gray-50 p-3 text-xs text-gray-600">
              {fetching ? (
                "Fetching existing configuration..."
              ) : existingConfig ? (
                <>
                  Existing Config ID: <b>{existingConfig.pk_config_id}</b> • Created:{" "}
                  <b>{existingConfig.created_on ? String(existingConfig.created_on) : "-"}</b>
                </>
              ) : selectedDriverCode ? (
                "No existing config found. You can add a new one."
              ) : (
                "Select a driver to start."
              )}
            </div>
          </div>

          <div className="px-5 py-4 border-t flex items-center justify-between">
            <button
              className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Close
            </button>

            <div className="flex items-center gap-2">
              <button
                className="inline-flex items-center gap-2 px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm disabled:opacity-50"
                onClick={handleDelete}
                disabled={saving || !existingConfig}
                title={!existingConfig ? "No config to delete" : "Delete config"}
              >
                <Trash size={16} />
                Delete
              </button>

              <button
                className="inline-flex items-center gap-2 px-3 py-2 rounded bg-orange-600 hover:bg-orange-700 text-white text-sm disabled:opacity-50"
                onClick={handleSave}
                disabled={saving}
              >
                <Save size={16} />
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div className="flex flex-col">
      <label className="text-xs font-semibold text-gray-700 mb-1">{label}</label>
      <input
        className="border rounded px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter value"
        autoComplete="off"
      />
    </div>
  );
}
